import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/compat/router';
import { useIdleTimerContext } from 'react-idle-timer';
import { useObserver } from 'mobx-react-lite';
import { useApolloClient } from '@apollo/client';

import type {
  AddToCartInput,
  AddToCartResult,
  AdditionalRoutableParameters,
  CommerceActions,
  ConciergeAddToCartSurface,
  ConciergeTelemetryEvents,
  MenuType,
  TrackEventFn,
} from 'src/concierge/contract';
import { tracker } from 'src/utils/analytics';
import type { PayloadHandlers } from 'src/utils/analytics/helpers/fan-out';
import { GTM_V2_FLAG } from 'src/utils/analytics/constants/feature-flags';
import { PageTypes } from 'src/utils/analytics/trackers/internal-gtm-tracker/helpers/get-page-type';
import {
  ProductRecommendationSection,
  ProductRecommendationSource,
} from 'src/utils/analytics/trackers/internal-gtm-tracker/internal-gtm-tracker.types';
import { useBridgeActions } from 'src/theme/provider/commerce-components/bridge/bridge-actions';
import { useHeader } from 'src/dispensary/core-menu/components/header/use-header';
import useUI from 'hooks/use-ui';
import { useGetSessionId } from 'src/components/kiosk-pro/kiosk-pro-shared/use-session-id';
import { getLogger } from 'src/utils/datadog-logger';
import useSwitchMenuType from 'hooks/use-switch-menu-type';
import useAddToCart from 'src/hooks/use-add-to-cart';
import useCart from 'src/hooks/use-cart';
import useUser from 'src/hooks/use-user';
import { useModals } from 'src/components/modals/use-modals';
import { teardownKioskSession } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-session-teardown';
import useCustomerState from 'src/hooks/use-customer-state';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import useErnie from 'shared/hooks/use-ernie';
import { getMarketingParams } from 'shared/utils/get-marketing-params';
import { getPersistedValue } from 'shared/utils/persisted-values';
import { parseProduct } from 'src/utils/helpers/product/product';
import { cartItemId } from 'src/utils/helpers/product';
import type { Product } from 'src/utils/helpers/product';
import { checkAllLimits } from 'shared/helpers/purchase-limits';
import {
  isMultiSubcategoryPurchaseLimitsEnabled,
  isConfigurablePurchaseLimitsEnabled,
} from 'state/purchase-limits-flag';
import type { useUserButton } from 'src/dispensary/core-menu/components/header/user-button/use-user-button';
import { consumeConciergeReferrer } from 'src/concierge/concierge-referrer';
import { consumeInitialConciergeMessageSeed } from 'src/concierge/concierge-message-seed-storage';

type UserButton = ReturnType<typeof useUserButton>;

// Checked narrowing (assignment, not a cast): the concierge event entries in `Events`
// alias the contract map, so the fan-out tracker satisfies this mapped type. It lets
// trackEvent's generic (type, payload) pair correlate with the per-event handler.
const conciergeTracker: PayloadHandlers<ConciergeTelemetryEvents, void> = tracker;

// The client sends a semantic surface key; the ecomm `Page > Source > Section`
// attribution convention is owned here. Built from the enums so the section
// vocabulary has a single source of truth.
const TRACKER_SOURCE_BY_SURFACE: Record<ConciergeAddToCartSurface, string> = {
  product_recommendations: `${PageTypes.conciergePage} > ${ProductRecommendationSource.native} > ${ProductRecommendationSection.productRecommendations}`,
  bundle: `${PageTypes.conciergePage} > ${ProductRecommendationSource.native} > ${ProductRecommendationSection.bundle}`,
  agent_invoked: `${PageTypes.conciergePage} > ${ProductRecommendationSource.native} > ${ProductRecommendationSection.agentInvoked}`,
};

type IdleTimerRef = { current: { isIdle?: () => boolean; reset: () => void } | null | undefined };

/**
 * The bridge's idle-timer reset. While the timer is idle the shared idle warning is open and
 * has paused it, so a ping must not restart (and so un-pause) the timer under that warning.
 */
export function createKioskIdleTimerReset(idleTimerRef: IdleTimerRef): () => void {
  return (): void => {
    if (idleTimerRef.current?.isIdle?.()) return;
    idleTimerRef.current?.reset();
  };
}

const hostLogger = getLogger('concierge-host');

export function useCommerceActions(userButton: UserButton): CommerceActions {
  const sdkActions = useBridgeActions();
  const router = useRouter();
  const publicLoginSessionId = useGetSessionId();
  const { trackEvent: trackHeaderEvent } = useHeader();
  const switchMenuTypeHandler = useSwitchMenuType();
  const Cart = useCart();
  const { dispensary } = useDispensary();
  // Ecomm's full-validation add-to-cart path (same hook the PDP uses).
  const ecommAddToCart = useAddToCart(dispensary);
  const showErnie = useErnie();
  const customerState = useCustomerState();
  const flags = useFlags();
  const UI = useUI();
  const isKiosk = useObserver(() => UI?.isKiosk ?? false);
  const isKioskPro = useKioskPro();
  const isKioskProTerminal = isKiosk && isKioskPro;
  const User = useUser();
  const modals = useModals();
  const apolloClient = useApolloClient();
  // Null when no IdleTimerProvider is mounted above — non-kiosk surfaces, and
  // kiosk with the store's timeout disabled — despite the IIdleTimer typing.
  const idleTimer = useIdleTimerContext();
  const idleTimerRef = useRef(idleTimer);
  useEffect(() => {
    idleTimerRef.current = idleTimer;
  });
  const hasIdleTimer = idleTimer != null;
  // The host's kiosk idle timer, handed to the remote so a streaming response
  // counts as activity. Ref-backed so the handle identity survives provider
  // re-renders: the remote gets one stable object, not a new prop per render.
  const kioskIdleTimer = useMemo(
    () =>
      isKiosk && hasIdleTimer
        ? {
            reset: createKioskIdleTimerReset(idleTimerRef),
          }
        : undefined,
    [isKiosk, hasIdleTimer]
  );
  const isGtmV2 = !!flags[GTM_V2_FLAG];

  // The bridge reproduces the PDP add-to-cart composition (use-add-to-cart-controls)
  // imperatively: useAddToCart on its own runs med/rec + order-type handling but
  // does NOT enforce purchase limits, nor does it emit the telemetry
  // addedProductToCart event (only the legacy GA4 datalayer). Both are added here.
  // Source of truth for parity: use-add-to-cart-controls + usePrerequisiteChecks.
  //
  // cf-agents ships the raw GraphQL product shape on `input.product`; we run
  // `parseProduct` here at the bridge boundary — same normalization step
  // every other add-to-cart path in marketplace already does (PDP, reorder,
  // cart restoration, sponsored ads). No host-side fetch.
  const addToCart = useCallback(
    async (input: AddToCartInput): Promise<AddToCartResult> => {
      const product = parseProduct(input.product) as Product | undefined;
      if (!product) {
        showErnie('This product is no longer available.', 'danger');
        return { ok: false, reason: 'not_available' };
      }

      const { analyticsContext } = input;
      const trackerSource = TRACKER_SOURCE_BY_SURFACE[analyticsContext?.surface ?? 'product_recommendations'];

      // Purchase-limit gate
      const itemBeingAdded = {
        product,
        option: input.option,
        quantity: input.quantity,
        additionalOption: input.additionalOption,
        dispensary,
        trackerSource,
      };
      if (input.option) {
        const limits = checkAllLimits({
          newItemId: cartItemId(itemBeingAdded),
          itemBeingAdded,
          existingItems: Cart.items,
          medical: Cart.isMedical,
          dispensary,
          customerState,
          useMultiSubcategories: isMultiSubcategoryPurchaseLimitsEnabled(dispensary?.id),
          configurablePurchaseLimitsFeatureEnabled: isConfigurablePurchaseLimitsEnabled(dispensary?.id),
        });
        if (!limits.withinLimits) {
          showErnie(limits.message);
          return { ok: false, reason: 'limit_exceeded', message: limits.message };
        }
      }

      try {
        const { wasSuccessfullyAdded } = await ecommAddToCart({
          product,
          option: input.option,
          quantity: input.quantity,
          additionalOption: input.additionalOption,
          trackerSource,
        });
        if (!wasSuccessfullyAdded) {
          return { ok: false, reason: 'not_available' };
        }
        // Emit the telemetry addedProductToCart exactly as use-add-to-cart-controls
        // does. GTM v2 defers via the cart-change monitor so the ecommerce value
        // reflects server-calculated totals; otherwise fire directly.
        const payload = {
          quantity: input.quantity,
          product,
          option: input.option,
          trackerSource,
          position: analyticsContext?.position,
        };
        if (isGtmV2) {
          Cart.addPendingAnalyticsEvent({ type: 'add', payload });
        } else {
          tracker.addedProductToCart(payload);
        }
        return { ok: true };
      } catch {
        // useAddToCart surfaces some failures via Ernie internally, but
        // validation rejections + network errors can still bubble. Catch so
        // the MF remote doesn't see an unhandled rejection.
        showErnie('Something went wrong adding this item.', 'danger');
        return { ok: false, reason: 'error' };
      }
    },
    [ecommAddToCart, showErnie, dispensary, customerState, Cart, isGtmV2]
  );

  const switchMenuType = useCallback(
    async (type: MenuType): Promise<void> => {
      await switchMenuTypeHandler(type);
      trackHeaderEvent(`menu type > ${type}`);
    },
    [switchMenuTypeHandler, trackHeaderEvent]
  );

  const onAccountOpen = useCallback((): void => {
    trackHeaderEvent('Account Button');
  }, [trackHeaderEvent]);

  // Kiosk Pro logs in through its own modal, which needs a public-context session id;
  // the layout-registered handler creates it. The web login modal is not used there.
  const { handleLogin } = userButton;
  // The Concierge cannot see why a Kiosk Pro login did not start, so the host logs the reason.
  const goToLogin = useCallback(async (): Promise<void> => {
    if (isKioskProTerminal) {
      // Already logging in: a second request would swap the QR code mid-login. The modal renders
      // only with a session id, so a flag left set by a failed session request does not block.
      if (UI.isKioskProLoginModalOpen && publicLoginSessionId) return;
      let skipReason: 'faster-checkout-disabled' | 'layout-not-mounted' | 'no-dispensary' | null = null;
      if (!dispensary?.id) {
        skipReason = 'no-dispensary';
      } else if (dispensary.orderTypesConfigV2?.kiosk?.disableFasterCheckout) {
        skipReason = 'faster-checkout-disabled';
      } else if (!UI.kioskProLoginRequestHandler) {
        skipReason = 'layout-not-mounted';
      }
      if (skipReason) {
        hostLogger.warn('Kiosk Pro login request skipped', { reason: skipReason });
        return;
      }
      await UI.kioskProLoginRequestHandler?.();
      return;
    }
    await handleLogin();
  }, [isKioskProTerminal, UI, handleLogin, dispensary, publicLoginSessionId]);

  // Kiosk Pro logout is the standard End Session: full teardown, then back to the lock
  // screen (mirrors confirmedStartNewSession in use-kiosk-pro-layout).
  const { handleLogout } = userButton;
  const logout = useCallback(async (): Promise<void> => {
    if (!isKioskProTerminal) {
      await handleLogout();
      return;
    }
    try {
      await teardownKioskSession({
        user: User,
        cart: Cart,
        ui: { setIsKioskProLoginModalOpen: UI.setIsKioskProLoginModalOpen },
        apolloClient,
        idleTimer: idleTimerRef.current,
        dispensaryId: dispensary?.id,
        modals,
      });
      void router?.push(
        `/${String(UI.dispensaryRootUrl)}/[cName]/lock`,
        `/${String(UI.dispensaryRootUrl)}/${String(dispensary?.cName ?? '')}/lock`
      );
    } catch (error) {
      console.error('Failed to end the Kiosk Pro session:', error);
    }
  }, [
    isKioskProTerminal,
    handleLogout,
    User,
    Cart,
    UI,
    apolloClient,
    dispensary?.id,
    dispensary?.cName,
    modals,
    router,
  ]);

  const trackEvent: TrackEventFn = useCallback((type, payload) => {
    conciergeTracker[type](payload);
  }, []);

  // ECOMM-12848: returns to the stored referrer, if any is valid for this
  // dispensary; otherwise falls back to the sdk default (also covers router
  // being unavailable, so a click never silently no-ops).
  const goToStoreFront = useCallback(
    (params?: AdditionalRoutableParameters): void => {
      const referrerPath = consumeConciergeReferrer(dispensary?.id);
      if (!referrerPath || !router) {
        sdkActions.goToStoreFront(params);
        return;
      }

      // referrer's own query wins over marketing/extra params on collision
      // (matches appendMarketingParams' contract).
      const utmSessionData = getPersistedValue('utmSessionData', null);
      const marketingParams = getMarketingParams(router, utmSessionData);
      const [pathname, existingQuery = ''] = referrerPath.split('?');
      const searchParams = new URLSearchParams(existingQuery);
      Object.entries({ ...marketingParams, ...params?.query }).forEach(([key, value]) => {
        if (value !== undefined && !searchParams.has(key)) searchParams.set(key, value);
      });
      const queryString = searchParams.toString();
      void router.push(queryString ? `${pathname}?${queryString}` : pathname);
    },
    [router, sdkActions, dispensary?.id]
  );

  // Mirrors marketplace's cart-item — `lineKey` is the same compound key
  // (`${productId}-${option}-${additionalOption}`) computed when the line
  // was first added; cf-agents holds it from `bridge.commerce.cart.items`
  // and passes it back here.
  const updateCartItemQuantity = useCallback(
    (lineKey: string, quantity: number): void => {
      Cart.changeItemQuantity(lineKey, quantity, showErnie, customerState);
    },
    [Cart, showErnie, customerState]
  );

  const updateCartItemWeight = useCallback(
    (lineKey: string, weight: string): void => {
      const storeItem = Cart.items[lineKey];
      if (!storeItem) return;
      Cart.changeItemWeight(storeItem, weight, showErnie, customerState);
    },
    [Cart, showErnie, customerState]
  );

  const removeFromCart = useCallback(
    (lineKey: string): void => {
      Cart.removeItem(lineKey);
    },
    [Cart]
  );

  // Scoped to the current dispensary at call time (not the write-time
  // dispensary) so a staged seed can never be consumed after a store switch.
  const dispensaryId = dispensary?.id;
  const consumeInitialConciergeMessageSeedAction = useCallback(() => consumeInitialConciergeMessageSeed(dispensaryId), [
    dispensaryId,
  ]);

  return useMemo(
    () => ({
      ...sdkActions,
      addToCart,
      consumeInitialConciergeMessageSeed: consumeInitialConciergeMessageSeedAction,
      goToLogin,
      goToStoreFront,
      kioskIdleTimer,
      logout,
      onAccountOpen,
      removeFromCart,
      switchMenuType,
      trackEvent,
      updateCartItemQuantity,
      updateCartItemWeight,
    }),
    [
      sdkActions,
      addToCart,
      consumeInitialConciergeMessageSeedAction,
      goToStoreFront,
      kioskIdleTimer,
      goToLogin,
      logout,
      onAccountOpen,
      removeFromCart,
      switchMenuType,
      trackEvent,
      updateCartItemQuantity,
      updateCartItemWeight,
    ]
  );
}
